import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { AxiosResponse } from 'axios';
import { useToast } from '@chakra-ui/react';
import {
  deleteDocument,
  downloadBackgroundDocuments,
  getAllDocumentsTypes,
  getDocumentsByContractId,
  getDocumentsList,
  getDocumentsTypeAsSelect,
  getDocumentsTypes,
} from '~/services/Documents';
import { Documents, DocumentsType } from '~/types/documents';
import { Pagination, SelectItem } from '~/types';
import { QueryFilterContext } from '~/hooks/useFilter/types';

export function useDocumentsTypesAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
): UseQueryResult<SelectItem[], unknown> {
  return useQuery<SelectItem[], unknown>(
    ['documentsType'],
    () => getDocumentsTypeAsSelect(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useDocumentsTypes(
  options: UseQueryOptions<DocumentsType[], unknown> = {},
): UseQueryResult<DocumentsType[], unknown> {
  return useQuery<DocumentsType[], unknown>(
    ['documentsType'],
    () => getDocumentsTypes(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useAllDocumentsTypes(
  options: UseQueryOptions<Pagination<DocumentsType>, unknown> = {},
): UseQueryResult<Pagination<DocumentsType>, unknown> {
  return useQuery<Pagination<DocumentsType>, unknown>(
    ['allDocumentsType'],
    () => getAllDocumentsTypes(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useDocumentsList(
  page: number,
  orderby = '',
  order = '',
  search: string,
  ids_tipos: number[],
  query: QueryFilterContext,
  options: UseQueryOptions<Pagination<Documents>, unknown> = {},
  pageSize: number,
): UseQueryResult<Pagination<Documents>, unknown> {
  const arrGroups = query.groups?.map(e => e.value);
  const arrHeadOffices = query.headOffices?.map(e => e.value);
  const arrBrancches = query.branches?.map(e => e.value);
  return useQuery<Pagination<Documents>, unknown>(
    [
      'documentsList',
      page,
      orderby,
      order,
      search,
      ids_tipos,
      { arrGroups, arrHeadOffices, arrBrancches },
      pageSize,
    ],
    () =>
      getDocumentsList(
        page,
        orderby,
        order,
        search,
        ids_tipos,
        {
          groups: arrGroups,
          headoffices: arrHeadOffices,
          branches: arrBrancches,
        },
        pageSize,
      ),
    {
      staleTime: 1000 * 30,
      ...options,
    },
  );
}

export function useDocumentsByContractId(
  id: number,
  options: UseQueryOptions<Documents[]> = {},
): UseQueryResult<Documents[]> {
  return useQuery<Documents[]>(
    ['documentsList', id],
    () => getDocumentsByContractId(id),
    {
      staleTime: 1000 * 30,
      ...options,
    },
  );
}

export function useDocumentDelete(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  number,
  unknown
> {
  const toast = useToast();

  return useMutation(async (id: number) => deleteDocument(id), {
    onSuccess: () => {
      toast({
        title: 'Removido com sucesso',
        description: 'Documento removido com sucesso!',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Erro ao remover documento, tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}

export function useDownloadBackgroundDocument(
  isActive: boolean,
  docUUID: string,
  options: UseQueryOptions<any> = {},
): UseQueryResult<any> {
  const toast = useToast();
  return useQuery<any>(
    ['downloadBackgroundDocuments'],
    () => downloadBackgroundDocuments(docUUID),
    {
      staleTime: 1000 * 5,
      refetchInterval: isActive ? 1000 * 5 : false,
      enabled: isActive,
      ...options,
    },
  );
}
